/**
 * Phase 2R-CityMoments — "Right Now in [City]" ambient newsroom strip.
 *
 * Design constraints (CEO):
 *   - ambient newsroom, NOT dashboard
 *   - cinematic typography
 *   - restrained motion
 *   - live glow only on truly active moments
 *   - subtle locality labeling
 *   - warm editorial pacing
 *   - 1 hero + 3 secondary + 1 quietly rising  (= editorial scarcity)
 *
 * Reads: GET /api/arenax/city/:city/right-now?sport=...
 * No state mutation. Polls every 60s (significance changes slowly).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const POLL_MS = 60000;

function LiveDot() {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]"
      style={{ color: '#f97316' }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: '#f97316', boxShadow: '0 0 10px #f97316' }}
      />
      Live
    </span>
  );
}

function LocalityLabel({ moment }) {
  if (!moment?.locality && !moment?.sport && !moment?.city) return null;
  const bits = [];
  if (moment.locality) bits.push(moment.locality);
  if (moment.sport) bits.push(moment.sport);
  return (
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{bits.join(' · ')}</div>
  );
}

function HeroCard({ moment }) {
  if (!moment) return null;
  const content = (
    <div
      className="relative overflow-hidden p-7 md:p-9"
      style={{
        background: 'var(--ax-right-now-hero-bg)',
        borderRadius: '6px',
        border: '1px solid var(--ax-right-now-border)',
        backdropFilter: 'blur(14px)',
        boxShadow: 'var(--ax-shadow-card)',
      }}
      data-testid="arenax-right-now-hero"
    >
      {moment.is_live && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 200px at 0% 0%, rgba(249,115,22,0.08), transparent 60%)',
            opacity: 0.9,
          }}
        />
      )}
      <div className="relative z-10 space-y-3">
        <div className="flex items-center gap-4">
          <LocalityLabel moment={moment} />
          {moment.is_live && <LiveDot />}
        </div>
        <p
          className="text-[22px] md:text-[28px] leading-[1.3] font-light text-slate-100 tracking-tight"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          {moment.narrative}
        </p>
      </div>
    </div>
  );
  if (moment.deeplink) {
    return (
      <Link to={moment.deeplink} className="block transition-opacity duration-500 hover:opacity-90">
        {content}
      </Link>
    );
  }
  return content;
}

function SecondaryCard({ moment }) {
  if (!moment) return null;
  const content = (
    <div
      className="p-4 h-full"
      style={{
        background: 'var(--ax-right-now-secondary-bg)',
        borderRadius: '4px',
        border: '1px solid var(--ax-right-now-border)',
        boxShadow: 'var(--ax-shadow-card)',
      }}
      data-testid={`arenax-right-now-secondary-${moment.match_id || moment.clip_id || moment.moment_type}`}
    >
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <LocalityLabel moment={moment} />
          {moment.is_live && <LiveDot />}
        </div>
        <p
          className="text-[14px] leading-[1.4] text-slate-300"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          {moment.narrative}
        </p>
      </div>
    </div>
  );
  return moment.deeplink ? (
    <Link to={moment.deeplink} className="block transition-opacity duration-500 hover:opacity-90 h-full">
      {content}
    </Link>
  ) : (
    content
  );
}

function QuietlyRising({ moment }) {
  if (!moment) return null;
  const content = (
    <div className="flex items-center gap-3 text-[12px] text-slate-400 italic" data-testid="arenax-right-now-quietly-rising">
      <span
        className="inline-block w-1 h-1 rounded-full"
        style={{ background: '#94a3b8' }}
      />
      <span>{moment.narrative}</span>
    </div>
  );
  return moment.deeplink ? (
    <Link to={moment.deeplink} className="block hover:text-slate-300 transition-colors duration-500">
      {content}
    </Link>
  ) : (
    content
  );
}

export default function ArenaxRightNow({ city = 'Bengaluru', sport = null }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const params = sport ? `?sport=${encodeURIComponent(sport)}` : '';
        const r = await api.get(`/arenax/city/${encodeURIComponent(city)}/right-now${params}`);
        if (stopped) return;
        setData(r.data);
        setLoaded(true);
      } catch {
        // Silent failure — ambient newsroom never shows error noise.
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [city, sport]);

  if (!loaded) {
    return (
      <div className="py-12 text-center text-[11px] text-slate-600 tracking-[0.25em] uppercase" data-testid="arenax-right-now-loading">
        Reading the city…
      </div>
    );
  }

  const hasAnything = !!(data?.hero || data?.secondary?.length || data?.quietly_rising);
  if (!hasAnything) {
    return (
      <div className="py-12 text-[12px] text-slate-500 text-center italic" data-testid="arenax-right-now-quiet">
        {city} is quiet tonight.
      </div>
    );
  }

  return (
    <section
      className="space-y-6"
      data-testid="arenax-right-now-root"
      aria-label={`Right now in ${city}`}
    >
      <header className="flex items-baseline gap-3 px-1">
        <h2
          className="text-[11px] uppercase tracking-[0.32em] text-slate-500"
          data-testid="arenax-right-now-header"
        >
          Right now in {city}
        </h2>
        <span className="text-[10px] text-slate-700 tabular-nums">
          {sport ? `· ${sport}` : ''}
        </span>
      </header>
      <HeroCard moment={data.hero} />
      {data.secondary?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.secondary.map((m, i) => (
            <SecondaryCard key={m.match_id || m.clip_id || i} moment={m} />
          ))}
        </div>
      )}
      <QuietlyRising moment={data.quietly_rising} />
    </section>
  );
}
